//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';

import {
  AdobeCompany,
  GatewayConnectionStatus,
  IAdobeAuthenticationCredentials,
  IGetAdobeSettingsResponse,
  IGetCuralatePixelSettingsResponse,
  IGetGA4BigQueryDatasetResponse,
  IGetGA4ResponseMetadata,
  IGetGA4SettingsResponse,
} from '@apis/analytics-settings/analyticsSettingsApiModel';
import GaProfileId from '@models/GaProfileId';
import {
  serializedGatewayFetch,
  serializedSessionFetch,
} from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

import { crl8 } from 'app/curalate.globals';

const BASE_URL = '/api/settings';
const GA_URL = `/ga`;
const GA4_URL = `/ga4`;

function settingsFetch(endpoint: string, init?: RequestInit) {
  return serializedSessionFetch(`${BASE_URL}${endpoint}`, init);
}

// TODO: PD-319318 - Remove AnalyticsConfigurationMigration feature flag
// TODO: PD-319995: Clean up old services flow and update to call the config gateway directly with a dedicated api file
const isAnalyticsConfigMigrationEnabled = (): boolean => {
  return !!crl8.session?.features[FeatureNames.AnalyticsConfigurationMigration];
};

const webGatewayAnalyticsProvidersFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v1/settings/analytics-providers${endpoint}`,
    init,
  );
};

const analyticsConfigGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_analytics_configuration_gateway',
    `/api/v1${endpoint}`,
    init,
  );
};

/**
 * Gets the current client's Google Analytics settings
 *
 * GET /api/settings/ga4
 */
export const getGA4Settings = async (
  init?: RequestInit,
): Promise<IGetGA4SettingsResponse> => {
  if (isAnalyticsConfigMigrationEnabled()) {
    const res = await analyticsConfigGatewayFetch('/google/analytics', init);
    const json = await res.json<{
      data: { connectionStatus: GatewayConnectionStatus };
    }>();
    const { connectionStatus } = json.data;
    return {
      currentPropertyId: connectionStatus.profileId,
      properties: connectionStatus.profiles,
    };
  } else {
    const res = await settingsFetch(GA4_URL, init);
    return await res.json<IGetGA4SettingsResponse>();
  }
};

/**
 * Gets the current client's Google BigQuery settings
 *
 * GET /api/settings/ga4/bigquery
 */
export const getGA4BigQueryDataset = async (
  init?: RequestInit,
): Promise<IGetGA4BigQueryDatasetResponse> => {
  if (isAnalyticsConfigMigrationEnabled()) {
    const res = await analyticsConfigGatewayFetch('/google/bigquery', init);
    const json = await res.json<{
      data: { bigQueryData: IGetGA4BigQueryDatasetResponse };
    }>();
    return json.data.bigQueryData;
  } else {
    const res = await settingsFetch(`${GA4_URL}/bigquery`);
    return res.json();
  }
};

export const getGA4ResponseMetadata = async (
  profileId: GaProfileId,
): Promise<IGetGA4ResponseMetadata[]> => {
  const res = await settingsFetch(
    `${GA4_URL}/metadata/${encodeURIComponent(profileId)}`,
  );
  const json: { data: IGetGA4ResponseMetadata[] } = await res.json();
  return json.data;
};

/**
 * Delete the client's Google BigQuery settings
 *
 * DELETE /api/settings/ga4/bigquery
 */
export const deleteGA4BigQueryDataset = async (
  init?: RequestInit,
): Promise<void> => {
  if (isAnalyticsConfigMigrationEnabled()) {
    await analyticsConfigGatewayFetch('/google/bigquery', {
      ...init,
      method: 'DELETE',
    });
  } else {
    await settingsFetch(`${GA4_URL}/bigquery`, { method: 'DELETE' });
  }
};

/**
 * Create/Update the current client's Google BigQuery settings
 *
 * POST /api/settings/ga4/analytics-hub
 */
export const subscribeAnalyticsHubListing = async (
  url: string,
  init?: RequestInit,
): Promise<void> => {
  if (isAnalyticsConfigMigrationEnabled()) {
    await analyticsConfigGatewayFetch('/google/bigquery', {
      ...init,
      method: 'PUT',
      body: JSON.stringify({ url }),
    });
  } else {
    await settingsFetch(`${GA4_URL}/analytics-hub`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }
};

/**
 * Gets the current client's Curalate pixel info.
 *
 * GET /api/settings/pixel
 */
export const getCuralatePixelSettings = async (
  init?: RequestInit,
): Promise<IGetCuralatePixelSettingsResponse> => {
  const res = await settingsFetch('/pixel', init);
  return await res.json<IGetCuralatePixelSettingsResponse>();
};

/**
 * Gets the Google Analytics oauth url
 *
 * GET /api/settings/ga
 */
export const getGAOAuthURL = async (
  init?: RequestInit,
): Promise<{ url?: string }> => {
  if (isAnalyticsConfigMigrationEnabled()) {
    const res = await analyticsConfigGatewayFetch('/google/analytics/url', {
      ...init,
      method: 'POST',
      body: JSON.stringify({
        redirectUrl: `${window.location.origin}/google/oauth`,
      }),
    });
    const json = await res.json<{ data: { url: string } }>();
    return { url: json.data.url };
  } else {
    const res = await settingsFetch(`${GA_URL}/oauth`);
    return await res.json();
  }
};

/**
 * disconnects the clients Google Analytics account
 *
 * DELETE /api/settings/ga/deauth
 */
export const deauthGAAccount = async (init?: RequestInit): Promise<void> => {
  if (isAnalyticsConfigMigrationEnabled()) {
    await analyticsConfigGatewayFetch('/google/analytics', {
      ...init,
      method: 'DELETE',
    });
  } else {
    await settingsFetch(`${GA_URL}/deauth`, { method: 'DELETE' });
  }
};

/**
 * Updates the clients Google Analytics profile
 *
 * PUT /api/settings/ga/profile/${profileId}
 */
export const updateGAProfile = async (
  profileId: string,
  init?: RequestInit,
): Promise<void> => {
  if (isAnalyticsConfigMigrationEnabled()) {
    await analyticsConfigGatewayFetch(
      `/google/analytics/profile/${profileId}`,
      { ...init, method: 'PUT' },
    );
  } else {
    await settingsFetch(`${GA_URL}/profile/${profileId}`, {
      method: 'PUT',
    });
  }
};

/**
 * Gets the current client's Adobe Analytics settings (web-gateway)
 */
export async function getAdobeWebServiceSettings(
  init?: RequestInit,
): Promise<IGetAdobeSettingsResponse> {
  const res = await webGatewayAnalyticsProvidersFetch(
    '/adobe/web-service',
    init,
  );
  return await res.json<IGetAdobeSettingsResponse>();
}

export async function getAdobeOAuthSettings(
  init?: RequestInit,
): Promise<IGetAdobeSettingsResponse> {
  if (isAnalyticsConfigMigrationEnabled()) {
    const res = await analyticsConfigGatewayFetch('/adobe/analytics', init);
    const json = await res.json<{
      data: {
        connectionStatus: {
          globalCompanyId?: string;
          isConnected: boolean;
          reportSuiteId?: string;
        };
      };
    }>();
    return {
      isConnected: json.data.connectionStatus.isConnected,
      globalCompanyId: json.data.connectionStatus.globalCompanyId,
      reportSuiteId: json.data.connectionStatus.reportSuiteId,
    };
  } else {
    const res = await webGatewayAnalyticsProvidersFetch('/adobe/oauth', init);
    return await res.json<IGetAdobeSettingsResponse>();
  }
}

export async function connectAdobeAnalyticsOAuth(
  request: IAdobeAuthenticationCredentials,
): Promise<void> {
  const opts = { body: JSON.stringify(request), method: 'POST' };
  if (isAnalyticsConfigMigrationEnabled()) {
    await analyticsConfigGatewayFetch('/adobe/analytics/oauth', opts);
  } else {
    await webGatewayAnalyticsProvidersFetch('/adobe/oauth/account', opts);
  }
}

/**
 * Disconnects a clients adobe analytics connection (web-gateway)
 */
export async function disconnectAdobeAnalyticsWebservice(): Promise<void> {
  await webGatewayAnalyticsProvidersFetch('/adobe/web-service/account', {
    method: 'DELETE',
  });
}

export async function disconnectAdobeAnalyticsOAuth(): Promise<void> {
  if (isAnalyticsConfigMigrationEnabled()) {
    await analyticsConfigGatewayFetch('/adobe/analytics', {
      method: 'DELETE',
    });
  } else {
    await webGatewayAnalyticsProvidersFetch('/adobe/oauth/account', {
      method: 'DELETE',
    });
  }
}
export const getAdobeCompanies = async (
  init?: RequestInit,
): Promise<AdobeCompany[]> => {
  const res = await analyticsConfigGatewayFetch(
    '/adobe/analytics/companies',
    init,
  );
  const json = await res.json<{
    data: { companies: AdobeCompany[] };
  }>();
  return json.data.companies;
};

export const updateAdobeGlobalCompanyId = async (
  globalCompanyId: string,
): Promise<void> => {
  await analyticsConfigGatewayFetch('/adobe/analytics/global-company', {
    method: 'PUT',
    body: JSON.stringify({ globalCompanyId }),
  });
};
